if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        const isWishlistPage = window?.templateSuffix === 'wishlist';

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.backInStockForm = this.querySelector('[data-back-in-stock]');
        this.isCartPage = document.body.classList.contains('template-cart');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.backInStockForm) this.initBackInStockForm();
        if (isWishlistPage) this.handleWishlistPage();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (!evt.target.checkValidity()) {
          evt.target.reportValidity();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const isWishlistPage = window?.templateSuffix === 'wishlist';
        let addedWishlistItemVariantId = null;
        if (isWishlistPage) {
          addedWishlistItemVariantId = this.submitButton.getAttribute('wishlist-variant-id');
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              /**
               * TODO: This is a temporary solution to display a more user-friendly error message when a product size hasn't been selected.
               */
              const errorMessage =
                response.description === 'Required parameter missing or invalid: id'
                  ? 'Please select a size'
                  : response.description;
              this.handleErrorMessage(errorMessage);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              if (!this.isCartPage) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                    });
                  },
                  { once: true }
                );
              }
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            if (addedWishlistItemVariantId) {
              const wishlistItemAddedToCartEvent = new CustomEvent('wishlistItemAddedToCart', {
                detail: { variantId: addedWishlistItemVariantId },
              });
              document.dispatchEvent(wishlistItemAddedToCartEvent);
            }
          });
      }

      initBackInStockForm() {
        const inputs = this.backInStockForm.querySelectorAll('input[type="email"], input[type="tel"]');
        inputs.forEach((input) => {
          input.addEventListener('input', () => {
            input.setCustomValidity('');
          });
          input.addEventListener(
            'keyup',
            function (evt) {
              if (evt.key !== 'Enter') return;
              this.handleBackInStockFormSubmit();
            }.bind(this)
          );
        });
        const submitButton = this.backInStockForm.querySelector('[type="button"]:not(.tabs__tab)');
        if (!submitButton) return;
        submitButton.addEventListener('click', this.handleBackInStockFormSubmit.bind(this));
      }

      async handleBackInStockFormSubmit() {
        const { publicApiKey, apiRevision, newsletterListId, backInStockStrings } = window.klaviyoSettings;

        const variantId = this.backInStockForm.closest('form')?.querySelector('[name="id"]')?.value;
        if (!variantId) return;

        const input = this.backInStockForm.querySelector('.tabs__tabpanel:not([hidden]) input');
        if (!input) return;

        const subscribeToNewsletter = Boolean(
          this.backInStockForm.querySelector('[name="subscribe_to_newsletter"]')?.checked
        );

        this.removeBackInStockFormMessages();

        const { name, value } = input;
        const isEmail = name === 'email';
        const invalidMessage = isEmail ? backInStockStrings.invalidEmail : backInStockStrings.invalidPhone;

        if (value) {
          input.setCustomValidity('');
        } else {
          input.setCustomValidity(invalidMessage);
          input.reportValidity();
          input.focus();
          return;
        }

        if (!input.checkValidity()) {
          if (!isEmail) {
            input.setCustomValidity(invalidMessage);
          }
          input.reportValidity();
          input.focus();
          return;
        }

        const options = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: apiRevision,
          },
        };

        const attributes = isEmail ? { email: value } : { phone_number: value };
        const channels = isEmail ? ['EMAIL'] : ['SMS'];

        const payload = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes,
                },
              },
              channels,
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        };

        try {
          if (subscribeToNewsletter) {
            const newsletterPayload = {
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'Back in stock subscription',
                  profile: {
                    data: {
                      type: 'profile',
                      attributes,
                    },
                  },
                },
                relationships: {
                  list: {
                    data: {
                      type: 'list',
                      id: newsletterListId,
                    },
                  },
                },
              },
            };

            fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${publicApiKey}`, {
              ...options,
              body: JSON.stringify(newsletterPayload),
            });
          }

          const response = await fetch(
            `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${publicApiKey}`,
            {
              ...options,
              body: JSON.stringify(payload),
            }
          );

          if (!response.ok || response.status !== 202) {
            this.handleBackInStockError(backInStockStrings);
            return;
          }

          this.handleBackInStockSuccess(backInStockStrings);
        } catch (error) {
          this.handleBackInStockError(backInStockStrings);
        }
      }

      handleBackInStockError(backInStockStrings) {
        const submitButton = this.backInStockForm.querySelector('[type="button"]:not(.tabs__tab)');
        if (!submitButton) return;

        let errorElement = this.backInStockForm.querySelector('.product-form__back-in-stock-error');
        if (errorElement) {
          errorElement.innerHTML = backInStockStrings.error;
          return;
        }

        errorElement = document.createElement('div');
        errorElement.classList.add('product-form__back-in-stock-error');
        errorElement.innerHTML = backInStockStrings.error;
        submitButton.insertAdjacentElement('beforebegin', errorElement);
      }

      handleBackInStockSuccess(backInStockStrings) {
        const inputs = this.backInStockForm.querySelectorAll('input[type="email"], input[type="tel"]');
        inputs.forEach((input) => {
          input.value = '';
        });

        const submitButton = this.backInStockForm.querySelector('[type="button"]:not(.tabs__tab)');
        if (!submitButton) return;

        let successElement = this.backInStockForm.querySelector('.product-form__back-in-stock-success');
        if (successElement) {
          successElement.innerHTML = backInStockStrings.success;
          return;
        }

        successElement = document.createElement('div');
        successElement.classList.add('product-form__back-in-stock-success');
        successElement.innerHTML = backInStockStrings.success;
        submitButton.insertAdjacentElement('beforebegin', successElement);
      }

      removeBackInStockFormMessages() {
        const errorElement = this.backInStockForm.querySelector('.product-form__back-in-stock-error');
        if (errorElement) errorElement.remove();

        const successElement = this.backInStockForm.querySelector('.product-form__back-in-stock-success');
        if (successElement) successElement.remove();
      }

      handleWishlistPage() {
        // Check if current form is a Wishlisted product form, and if so, update default CTA text since we know that wishlisted variant will be selected by default
        const selectedVariantId = this.querySelector('.product-variant-id').value;
        const isWishlistedProductForm = this.form.getAttribute('id').indexOf('wishlist') > -1;
        if (!isWishlistedProductForm) return;

        const submitButton = this.form.querySelector('[type="submit"]');
        const addButtonAvailableText = submitButton.querySelector('span > span:last-child');
        addButtonAvailableText.innerHTML = window.variantStrings.wishlist_add_to_cart || 'Move to cart';
        submitButton.setAttribute('wishlist-variant-id', selectedVariantId);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.innerHTML = errorMessage;
        }
      }
    }
  );
}
